import { Component, Input, OnChanges, OnDestroy, SimpleChanges, signal } from '@angular/core';

@Component({
  selector: 'app-live-waveform-display',
  standalone: true,
  imports: [],
  templateUrl: './live-waveform-display.component.html',
  styleUrls: ['../../waveform.scss'],
})
export class LiveWaveformDisplayComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) stream: MediaStream;

  public waveformBars = signal<{ height: number }[]>([]);

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animationFrameId: number | null = null;
  private readonly BARS_COUNT = 40;

  constructor() {
    this.waveformBars.set(Array(this.BARS_COUNT).fill({ height: 0 }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stream'] && this.stream) {
      this.initAudioVisualization();
    } else {
      this.stopVisualization();
    }
  }

  ngOnDestroy(): void {
    this.stopVisualization();
  }

  /**
   * Sets up the Web Audio API nodes to analyze the incoming audio stream.
   */
  private initAudioVisualization(): void {
    if (!this.stream || this.audioContext) return;

    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();

    // The FFT size determines how many data points are collected.
    this.analyser.fftSize = 256;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    // Create a source node from the microphone's MediaStream.
    this.source = this.audioContext.createMediaStreamSource(this.stream);

    // Connect the source to the analyser to start processing data.
    this.source.connect(this.analyser);

    // Start the animation loop.
    this.draw();
  }

  /**
   * The main animation loop that continuously draws the waveform.
   */
  private draw = () => {
    if (!this.analyser || !this.dataArray) return;

    // Request the next animation frame to create a smooth loop.
    this.animationFrameId = requestAnimationFrame(this.draw);

    // Get the frequency data from the analyser.
    this.analyser.getByteFrequencyData(this.dataArray);

    // Update the waveformBars signal with new heights based on the audio data.
    const newBars = [];
    const step = Math.floor(this.dataArray.length / this.BARS_COUNT);
    for (let i = 0; i < this.BARS_COUNT; i++) {
      const amplitude = this.dataArray[i * step] / 255; // Normalize to a 0-1 range
      const height = Math.pow(amplitude, 2) * 100; // Use a power scale for better visuals
      newBars.push({ height: Math.max(2, height) }); // Ensure a minimum height
    }
    this.waveformBars.set(newBars);
  };

  /**
   * Stops the animation and cleans up Web Audio API resources.
   */
  private stopVisualization(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    // Disconnect nodes and close the context to free up resources.
    this.source?.disconnect();
    this.source = null;
    this.analyser = null;
    this.dataArray = null;
    this.audioContext?.close().catch(console.error);
    this.audioContext = null;
    this.waveformBars.set(Array(this.BARS_COUNT).fill({ height: 0 }));
  }
}
